import { Constants } from 'utils';
class ShelfForm extends HTMLElement {
	connectedCallback() {
		this.form = this.querySelector('form');
		this.addLevelButton = this.querySelector('button[add-level]');
		this.setUpButton = this.querySelector('button[set-up]');
		this.shelfFormLevels = [...this.querySelectorAll('shelf-form-level')];
		this.maxLevels = parseInt(this.dataset.maxLevels) || 10;
		this.addLevelButton.addEventListener('click', this.addAnotherLevel.bind(this));
		this.setUpButton.addEventListener('click', this.setUp.bind(this));
		this.form.addEventListener('click', (e) => {
			if (e.target.closest('button[remove-level]')) {
				this.removeLastLevel(e);
			}
		});
		this._updateRemoveLevelButtons();
	}
	disconnectedCallback() {
		this.addLevelButton.removeEventListener('click', this.addAnotherLevel.bind(this));
		this.setUpButton.removeEventListener('click', this.setUp.bind(this));
	}
	_updateRemoveLevelButtons() {
		const lastLevel = this.shelfFormLevels.reduce(
			(max, level) => (Number.isFinite(level.levelIndex) && level.levelIndex > (max?.levelIndex ?? -Infinity) ? level : max),
			null,
		);
		if (!lastLevel) return;
		this.shelfFormLevels.forEach((level) => {
			const btn = level.querySelector('button[remove-level]');
			if (!btn) return;
			const levelIndex = Number.isFinite(level.levelIndex) ? level.levelIndex : null;
			if (!levelIndex || levelIndex === 1) {
				btn.classList.add('hide');
			} else {
				btn.classList.remove('hide');
				btn.disabled = level !== lastLevel;
			}
		});
		this.addLevelButton.classList.toggle('hide', this.shelfFormLevels.length >= this.maxLevels);
	}
	initWithLocalStorage({ levels, isShelfReady }) {
		const shelfFormLevelToClone = this.shelfFormLevels[0];
		const newLevels = [];
		levels.forEach((level, index) => {
			const newLevel = shelfFormLevelToClone.cloneNode(true);
			newLevel.initWithLocalStorage({
				level,
				lastLevelNode: index > 0 ? newLevels[index - 1] : null,
			});
			newLevels.push(newLevel);
		});
		this.shelfFormLevels.forEach((level) => {
			level.remove();
		});
		newLevels.reverse().forEach((newLevel, currentIndex) => {
			this.form.prepend(newLevel);
			// only the last level should be active (REVERSE ORDER )
			if (currentIndex > 0) {
				newLevel._deactivate();
			}
		});
		this.shelfFormLevels = [...this.querySelectorAll('shelf-form-level')];
		this._updateRemoveLevelButtons();
		if (isShelfReady) {
			const dummyEvent = new Event('dummy');
			this.setUp(dummyEvent);
		}
	}
	getValues() {
		return this.shelfFormLevels.map((ShelfFormLevel) => ShelfFormLevel.getValues());
	}
	addAnotherLevel(event) {
		// TODO CHECK IF BIN STACKING IS POSSIBLE
		event.preventDefault();
		if (!this.form.checkValidity()) {
			this.form.reportValidity();
			return;
		}
		const lastLevel = this.shelfFormLevels[this.shelfFormLevels.length - 1];
		const newLevel = lastLevel.cloneNode(true);
		newLevel._activate(lastLevel);
		lastLevel._deactivate();
		lastLevel.after(newLevel);
		this.shelfFormLevels.push(newLevel);
		this._updateRemoveLevelButtons();
	}
	removeLastLevel(event) {
		event.preventDefault();
		if (this.shelfFormLevels.length === 1) {
			return;
		}
		const lastLevel = this.shelfFormLevels.pop();
		lastLevel.remove();
		// activate the last level
		const newLastLevel = this.shelfFormLevels[this.shelfFormLevels.length - 1];
		newLastLevel?._enable();
		this._updateRemoveLevelButtons();
	}
	setUp(event) {
		event.preventDefault();
		if (!this.form.checkValidity()) {
			this.form.reportValidity();
			return;
		}
		// fire event to configurator
		this.dispatchEvent(
			new CustomEvent('shelf-form:set-up', {
				detail: {
					levels: this.getValues(),
				},
			}),
		);
	}
}
customElements.define('shelf-form', ShelfForm);
class ShelfFormLevel extends HTMLElement {
	constructor() {
		super();
		this.levelIndex = parseInt(this.dataset.level);
		this.counterNode = this.querySelector('[counter]');
		this.inputNodes = this.querySelectorAll('input');
		this.widthInput = this.querySelector('input[name="width"]');
		this.heightInput = this.querySelector('input[name="height"]');
		this.depthInput = this.querySelector('input[name="depth"]');
		this.levelPlacement = this.querySelector('select[name="placement"]');
		this.levelPlacementParent = this.levelPlacement.closest('.shelf-form__input-item');
		this.levelPlacement.addEventListener('change', this._changeInputLimits.bind(this));
	}
	connectedCallback() {
		this.levelIndex = parseInt(this.dataset.level);
		this.inputNodes.forEach((input) => {
			//  prevent the input from changing when scrolling above the input
			input.addEventListener('wheel', () => input.blur());
		});
		this._toggleSelector();
	}
	_activate(lastLevel) {
		this.lastLevelNode = lastLevel;
		this.setAttribute('data-level', this.levelIndex + 1);
		this.levelIndex = this.levelIndex + 1;
		this.counterNode.innerText = this.levelIndex;
		this.inputNodes.forEach((inputNode) => {
			inputNode.value = '';
			inputNode.disabled = false;
		});
		// depth is fixed to the last level's depth
		// width is should be at most the last level's width as bigger shelves are not allowed
		const { width: maxWidth, depth: maxDepth } = this.lastLevelNode.getValues();
		const maxWidthInCm = maxWidth / 10;
		const maxDepthInCm = maxDepth / 10;
		this.widthInput.max = maxWidthInCm;
		this.depthInput.value = maxDepthInCm;
		this.depthInput.disabled = true;
		this.depthInput.min = maxDepthInCm;
		this.depthInput.max = maxDepthInCm;
	}
	initWithLocalStorage(data) {
		const { index, width, height, depth, levelPlacement } = data.level;
		this.levelIndex = index;
		this.setAttribute('data-level', this.levelIndex);
		this.counterNode.innerText = this.levelIndex;
		this.widthInput.value = (width / 10).toFixed(1);
		this.heightInput.value = (height / 10).toFixed(1);
		this.depthInput.value = (depth / 10).toFixed(1);
		this.levelPlacement.value = levelPlacement;
		this.lastLevelNode = data.lastLevelNode;
	}
	_enable() {
		this.inputNodes.forEach((inputNode) => {
			inputNode.disabled = false;
		});
		this.levelPlacement.disabled = false;
	}
	_changeInputLimits(event) {
		const { width: maxWidth } = this.lastLevelNode.getValues();
		if (event.target.value === Constants.BIN_PLACEMENT_NEXT_TO) {
			this.widthInput.removeAttribute('max');
		} else {
			this.widthInput.max = maxWidth;
		}
	}
	_deactivate() {
		this.inputNodes.forEach((inputNode) => {
			inputNode.disabled = true;
		});
		this.levelPlacement.disabled = true;
	}
	_toggleSelector() {
		this.classList.toggle('shelf-form__level-wrapper--4', this.levelIndex != 1);
		this.levelPlacementParent.classList.toggle('hide', this.levelIndex === 1);
	}

	getValues() {
		return {
			index: this.levelIndex,
			width: parseFloat(this.widthInput.value) * 10,
			height: parseFloat(this.heightInput.value) * 10,
			depth: parseFloat(this.depthInput.value) * 10,
			levelPlacement: this.levelPlacement.value,
		};
	}
}
customElements.define('shelf-form-level', ShelfFormLevel);
